import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { WorkbookDefinition, WorkbookTable } from '@erp/shared-types';
import {
  definitionSourceColumns,
  planWorkbook,
  type WorkbookPlan,
} from '@erp/workbooks';
import { sourcePreviewQuery } from './useSourcePreview';

/** A Source preview some other view already loaded: its columns and row count. */
export type LoadedSourcePreview = WorkbookTable & { rowCount: number };

// Module-level so useQueries keeps the combined result stable between renders.
function previewData(results: readonly { data?: LoadedSourcePreview }[]) {
  return results.map((result) => result.data);
}

const remoteSources = (sources: WorkbookDefinition['sources']) =>
  sources.filter((source) => source.kind !== 'input');

/**
 * The previews already loaded for the Definition's non-inline Sources, by
 * Source id. Nothing is fetched here: a Source read can be expensive.
 */
export function useLoadedSourcePreviews(
  workbookId: string,
  definition: WorkbookDefinition,
): ReadonlyMap<string, LoadedSourcePreview> {
  const remote = remoteSources(definition.sources);
  const previews = useQueries({
    queries: remote.map((source) => ({
      ...sourcePreviewQuery(workbookId, source),
      enabled: false,
    })),
    combine: previewData,
  });
  return useMemo(() => {
    const loaded = new Map<string, LoadedSourcePreview>();
    remoteSources(definition.sources).forEach((source, index) => {
      const preview = previews[index];
      if (preview) loaded.set(source.id, preview);
    });
    return loaded;
  }, [definition.sources, previews]);
}

/**
 * Plans a Definition with the Source columns known on the page: inline Sources
 * bring their own columns; any other Source is planned with the columns of its
 * already-loaded preview, and as an unknown schema until one is loaded.
 */
function planWithPreviews(
  definition: WorkbookDefinition,
  previews: ReadonlyMap<string, LoadedSourcePreview>,
): WorkbookPlan {
  const known = definitionSourceColumns(definition);
  for (const [id, preview] of previews) known.set(id, preview.columns);
  return planWorkbook(definition, known);
}

/**
 * Plans the edited Definition live, unsaved edits included, and gives
 * `planOf` to plan another Definition (an agent's untried edit) the same way.
 */
export function useWorkbookPlan(
  workbookId: string,
  definition: WorkbookDefinition,
): {
  plan: WorkbookPlan;
  planOf: (definition: WorkbookDefinition) => WorkbookPlan;
} {
  const previews = useLoadedSourcePreviews(workbookId, definition);
  const plan = useMemo(
    () => planWithPreviews(definition, previews),
    [definition, previews],
  );
  return {
    plan,
    planOf: (other) => planWithPreviews(other, previews),
  };
}
